<template>
  <main dir="rtl" class="min-h-screen bg-slate-50 text-slate-900">
    <!-- Sticky header with controls -->
    <header class="bg-white border-b border-slate-200 sticky top-0 z-30">
      <div class="max-w-[1400px] mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <div class="bg-primary p-2 rounded-lg text-white">
            <span class="material-icons-round">calendar_today</span>
          </div>
          <div>
            <h1 class="text-xl font-bold">تقرير الجلسة اليومي</h1>
            <p v-if="selectedScheduleTitle" class="text-xs text-slate-500 mt-0.5">{{ selectedScheduleTitle }}</p>
          </div>
        </div>
        <div class="flex items-center gap-3 flex-wrap">
          <input
            v-model="selectedDate"
            type="date"
            @change="loadDailyReport"
            class="px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 focus:ring-primary focus:border-primary text-sm"
          />
          <select
            v-model="selectedScheduleId"
            class="min-w-[200px] px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:ring-primary focus:border-primary text-sm"
          >
            <option value="">اختر جدول اقرأ</option>
            <option v-for="sch in scheduleOptions" :key="sch.id" :value="sch.id">
              {{ sch.title }}
            </option>
          </select>
          <select
            v-model="selectedTeacherId"
            :disabled="!selectedScheduleId || teacherOptions.length === 0"
            class="min-w-[180px] px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:ring-primary focus:border-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">كل المعلمين</option>
            <option v-for="teacher in teacherOptions" :key="teacher.id" :value="String(teacher.id)">
              {{ teacher.name }}
            </option>
          </select>
          <button
            @click="printReport"
            class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <span class="material-icons-round text-lg">print</span>
            <span>طباعة</span>
          </button>
        </div>
      </div>
    </header>

    <section class="max-w-[1400px] mx-auto p-6">
      <!-- Loading state -->
      <div v-if="loading" class="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-600">
        <div class="inline-block animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent mb-4"></div>
        <p>جاري تحميل التقرير...</p>
      </div>

      <!-- Error state -->
      <div v-else-if="errorMessage" class="bg-red-50 border border-red-200 rounded-xl p-5 text-red-700">
        <div class="flex items-center gap-2">
          <span class="material-icons-round">error_outline</span>
          <span>{{ errorMessage }}</span>
        </div>
      </div>

      <!-- Prompt: no selection -->
      <div v-else-if="!selectedScheduleId" class="bg-white border border-slate-200 rounded-xl p-14 text-center text-slate-500">
        <span class="material-icons-round text-6xl text-slate-300 block mb-4">calendar_today</span>
        <p class="text-lg font-medium mb-1">اختر جدول اقرأ وتاريخاً لعرض التقرير اليومي</p>
        <p class="text-sm text-slate-400">سيتم عرض الطلاب المسجلين لكل صلاة في اليوم المحدد</p>
      </div>

      <!-- No schedule for this day -->
      <div
        v-else-if="scheduleDetail && !hasScheduleForDay"
        class="bg-white border border-slate-200 rounded-xl p-14 text-center text-slate-500"
      >
        <span class="material-icons-round text-6xl text-slate-300 block mb-4">event_busy</span>
        <p class="text-lg font-medium mb-1">
          لا يوجد جدول نشط ليوم <strong>{{ selectedDayLabel }}</strong>
        </p>
        <p class="text-sm text-slate-400">الجدول المختار لا يتضمن فترات فعّالة لهذا اليوم</p>
      </div>

      <!-- Daily report grid -->
      <template v-else-if="scheduleDetail && hasScheduleForDay">
        <!-- Summary cards -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 print-hide">
          <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-3">
            <div class="w-11 h-11 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span class="material-icons-round">people</span>
            </div>
            <div>
              <p class="text-xs text-slate-500">الطلاب المسجلون</p>
              <p class="text-xl font-bold">{{ totalStudents }}</p>
            </div>
          </div>
          <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-3">
            <div class="w-11 h-11 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span class="material-icons-round">event_note</span>
            </div>
            <div>
              <p class="text-xs text-slate-500">الفترات النشطة</p>
              <p class="text-xl font-bold">{{ totalSlots }}</p>
            </div>
          </div>
          <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-3">
            <div class="w-11 h-11 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span class="material-icons-round">school</span>
            </div>
            <div>
              <p class="text-xs text-slate-500">المعلمون</p>
              <p class="text-xl font-bold">{{ totalTeachers }}</p>
            </div>
          </div>
          <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-3">
            <div class="w-11 h-11 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span class="material-icons-round">calendar_today</span>
            </div>
            <div>
              <p class="text-xs text-slate-500">اليوم المعروض</p>
              <p class="text-sm font-bold text-primary">{{ selectedDayLabel }}</p>
              <p class="text-xs text-slate-400">{{ formattedDate }}</p>
            </div>
          </div>
        </div>

        <!-- Main daily grid: columns = period slots (based on max slots in any prayer) -->
        <div class="bg-white rounded-2xl border border-slate-200 overflow-x-auto overflow-hidden shadow-sm">
          <div
            v-if="maxSlotColumns > 0"
            class="daily-grid-cols border-b border-slate-200"
            :style="{ gridTemplateColumns: `110px repeat(${maxSlotColumns}, minmax(200px, 1fr))` }"
          >
            <!-- Header row -->
            <div class="p-4 bg-slate-50 border-l border-slate-200 font-bold text-center text-slate-500 text-sm">
              الصلاة
            </div>
            <div
              v-for="colIdx in slotColumnIndices"
              :key="'h-' + colIdx"
              class="p-3 bg-primary font-bold text-center text-white text-sm border-l border-white/20"
            >
              فترة {{ colIdx + 1 }}
            </div>
          </div>

          <!-- Prayer rows: one cell per period column -->
          <div
            v-for="prayer in visiblePrayerRows"
            :key="prayer.key"
            class="daily-grid-cols border-b border-slate-100 last:border-0 min-h-[100px]"
            :style="{ gridTemplateColumns: `110px repeat(${maxSlotColumns}, minmax(200px, 1fr))` }"
          >
            <div class="p-4 border-l border-slate-200 bg-slate-50 flex flex-col items-center justify-center">
              <span class="text-sm font-bold text-slate-700 text-center">{{ prayer.label }}</span>
            </div>
            <div
              v-for="colIdx in slotColumnIndices"
              :key="'c-' + prayer.key + '-' + colIdx"
              class="p-2 border-l border-slate-100 align-top"
            >
              <div
                v-if="slotGroupsByPrayer[prayer.key][colIdx]"
                :key="slotGroupsByPrayer[prayer.key][colIdx].slotTeacherId"
                class="rounded-lg border-r-4 p-3 h-full min-h-[80px]"
                :class="groupColorClass(slotGroupsByPrayer[prayer.key][colIdx].colorKey)"
              >
                <div class="flex items-center justify-end gap-1 mb-2">
                  <span class="text-xs text-slate-400 bg-white rounded px-1.5 py-0.5 border border-slate-200 whitespace-nowrap">
                    فترة {{ slotGroupsByPrayer[prayer.key][colIdx].slotOrder }}
                  </span>
                </div>
                <div v-if="slotGroupsByPrayer[prayer.key][colIdx].timeDesc" class="text-xs text-slate-500 mb-2 flex items-center gap-1">
                  <span class="material-icons-round flex-shrink-0" style="font-size: 12px;">schedule</span>
                  {{ slotGroupsByPrayer[prayer.key][colIdx].timeDesc }}
                </div>
                <div v-if="slotGroupsByPrayer[prayer.key][colIdx].students.length">
                  <div
                    v-for="student in slotGroupsByPrayer[prayer.key][colIdx].students"
                    :key="student.enrollmentId"
                    class="flex items-center gap-1.5 text-xs py-0.5"
                  >
                    <span class="material-icons-round text-slate-400 flex-shrink-0" style="font-size: 13px;">person</span>
                    <span>{{ student.studentName }}</span>
                  </div>
                </div>
                <div v-else class="flex items-center gap-1 text-xs text-slate-400 italic">
                  <span class="material-icons-round flex-shrink-0" style="font-size: 12px;">info</span>
                  فترة متاحة (بدون طلاب)
                </div>
              </div>
            </div>
          </div>

          <div v-if="!visiblePrayerRows.length" class="p-10 text-center text-slate-500">
            لا توجد فترات مرتبطة بصلوات لهذا اليوم.
          </div>
        </div>

        <!-- Footer info -->
        <p class="mt-4 text-xs text-slate-400 text-center print-hide">
          يتم عرض الطلاب المسجلين بحالة "نشط" فقط، والذين تاريخ تسجيلهم يشمل {{ formattedDate }}.
        </p>
      </template>
    </section>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import {
  listIqraSchedules,
  getIqraSchedule,
  listIqraStudentSlotEnrollments,
} from '@/services/iqraSchedules';

export default defineComponent({
  name: 'SessionReport',

  data() {
    return {
      selectedDate: new Date().toISOString().split('T')[0],
      selectedScheduleId: '',
      selectedTeacherId: '',
      loading: false,
      errorMessage: '',
      scheduleOptions: [],
      scheduleDetail: null,
      enrollments: [],
      prayerRows: [
        { key: 'fajr', label: 'صلاة الفجر', order: 1 },
        { key: 'dhuhr', label: 'صلاة الظهر', order: 2 },
        { key: 'asr', label: 'صلاة العصر', order: 3 },
        { key: 'maghrib', label: 'صلاة المغرب', order: 4 },
        { key: 'isha', label: 'صلاة العشاء', order: 5 },
        { key: 'fixed', label: 'أوقات ثابتة', order: 6 },
      ],
      weekdayLabels: {
        1: 'الإثنين',
        2: 'الثلاثاء',
        3: 'الأربعاء',
        4: 'الخميس',
        5: 'الجمعة',
        6: 'السبت',
        7: 'الأحد',
      },
    };
  },

  async mounted() {
    await this.loadScheduleOptions();
  },

  computed: {
    // Convert JS Date weekday to Iqra weekday: Sun=7, Mon=1...Sat=6
    selectedWeekday() {
      if (!this.selectedDate) return null;
      const d = new Date(`${this.selectedDate}T00:00:00`);
      const jsDay = d.getDay();
      return jsDay === 0 ? 7 : jsDay;
    },

    selectedDayLabel() {
      if (!this.selectedWeekday) return '';
      return this.weekdayLabels[this.selectedWeekday] || '';
    },

    formattedDate() {
      if (!this.selectedDate) return '';
      return new Date(`${this.selectedDate}T00:00:00`).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    selectedScheduleTitle() {
      if (!this.selectedScheduleId) return '';
      const sch = this.scheduleOptions.find((s) => s.id === this.selectedScheduleId);
      return sch?.title || '';
    },

    // True if the loaded schedule has at least one active day+slot+slotTeacher for the selected weekday
    hasScheduleForDay() {
      if (!this.scheduleDetail || !this.selectedWeekday) return false;
      return (this.scheduleDetail.days || []).some((day) => {
        if (day.is_active == false || day.is_active === '0' || day.weekday !== this.selectedWeekday) return false;
        return (day.slots || []).some((slot) => {
          if (slot.is_active == false || slot.is_active === '0') return false;
          return (slot.slot_teachers || []).some((st) => st.is_active != false && st.is_active !== '0');
        });
      });
    },

    // Unique list of teachers active in the selected schedule (across all days/slots)
    teacherOptions() {
      if (!this.scheduleDetail) return [];
      const seen = new Set();
      const teachers = [];

      const add = (id, name) => {
        const key = String(id);
        if (id && !seen.has(key)) {
          seen.add(key);
          teachers.push({ id: Number(id), name: name || `معلم #${id}` });
        }
      };

      // From schedule-level teachers list
      for (const t of this.scheduleDetail.teachers || []) {
        if (t.is_active == false || t.is_active === '0') continue;
        const teacher = t.teacher || t;
        add(teacher.id, teacher.full_name || teacher.fullName);
      }

      // From slot_teachers inside days/slots
      for (const day of this.scheduleDetail.days || []) {
        if (day.is_active == false || day.is_active === '0') continue;
        for (const slot of day.slots || []) {
          if (slot.is_active == false || slot.is_active === '0') continue;
          for (const st of slot.slot_teachers || []) {
            if (st.is_active == false || st.is_active === '0') continue;
            const t = st.teacher || {};
            add(st.teacher_id, t.full_name || t.fullName);
          }
        }
      }

      return teachers;
    },

    // Map: slot_teacher id -> metadata (prayer, slot info, teacher name)
    slotTeacherMap() {
      if (!this.scheduleDetail || !this.selectedWeekday) return {};
      const map = {};
      for (const day of this.scheduleDetail.days || []) {
        if (day.is_active == false || day.is_active === '0' || day.weekday !== this.selectedWeekday) continue;
        for (const slot of day.slots || []) {
          if (slot.is_active == false || slot.is_active === '0') continue;
          const prayerKey = this.normalizePrayerName(slot.prayer_name);
          const timeDesc = this.buildTimeDesc(slot);
          for (const st of slot.slot_teachers || []) {
            if (st.is_active == false || st.is_active === '0') continue;
            map[String(st.id)] = {
              slotTeacherId: st.id,
              prayerKey,
              slotOrder: slot.slot_order || 0,
              slotName: slot.slot_name || '',
              teacherName: st.teacher?.full_name || st.teacher?.fullName || 'غير محدد',
              teacherId: st.teacher_id,
              timeDesc,
            };
          }
        }
      }
      return map;
    },

    // Enrollments filtered by selected weekday + date range
    filteredEnrollments() {
      if (!this.selectedWeekday || !this.selectedDate) return [];
      return this.enrollments.filter((e) => {
        if (e.weekday !== this.selectedWeekday) return false;
        if (e.enrollment_start_date && e.enrollment_start_date > this.selectedDate) return false;
        if (e.enrollment_end_date && e.enrollment_end_date < this.selectedDate) return false;
        return true;
      });
    },

    // Groups all slot-teachers by prayer, with enrolled students attached
    slotGroupsByPrayer() {
      const map = this.slotTeacherMap;
      const groups = {};
      const teacherFilter = this.selectedTeacherId ? Number(this.selectedTeacherId) : null;

      // Initialize one group per slot-teacher from the schedule (filtered by teacher if selected)
      for (const [stId, info] of Object.entries(map)) {
        if (teacherFilter !== null && info.teacherId !== teacherFilter) continue;
        groups[stId] = {
          slotTeacherId: Number(stId),
          teacherName: info.teacherName,
          teacherId: info.teacherId,
          slotOrder: info.slotOrder,
          prayerKey: info.prayerKey,
          timeDesc: info.timeDesc,
          colorKey: this.colorFromName(info.teacherName),
          students: [],
        };
      }

      // Attach enrolled students to their slot-teacher group
      for (const enrollment of this.filteredEnrollments) {
        const stId = String(enrollment.slot_teacher_id);
        if (groups[stId]) {
          groups[stId].students.push({
            enrollmentId: enrollment.id,
            studentId: enrollment.student_id,
            studentName: enrollment.student_name || `طالب #${enrollment.student_id}`,
          });
        }
      }

      // Bucket by prayer key, sorted by slot order within each bucket
      const byPrayer = {};
      for (const group of Object.values(groups)) {
        const { prayerKey } = group;
        if (!byPrayer[prayerKey]) byPrayer[prayerKey] = [];
        byPrayer[prayerKey].push(group);
      }
      for (const key of Object.keys(byPrayer)) {
        byPrayer[key].sort((a, b) => a.slotOrder - b.slotOrder);
      }
      return byPrayer;
    },

    // Only show prayer rows that have at least one slot group
    visiblePrayerRows() {
      const byPrayer = this.slotGroupsByPrayer;
      return this.prayerRows.filter((p) => byPrayer[p.key] && byPrayer[p.key].length > 0);
    },

    // Max number of slots in any prayer row (determines number of columns)
    maxSlotColumns() {
      const byPrayer = this.slotGroupsByPrayer;
      let max = 0;
      for (const p of this.visiblePrayerRows) {
        const count = (byPrayer[p.key] || []).length;
        if (count > max) max = count;
      }
      return max;
    },

    // Column indices 0..maxSlotColumns-1 for header and cells
    slotColumnIndices() {
      return Array.from({ length: this.maxSlotColumns }, (_, i) => i);
    },

    totalStudents() {
      // Count students from the currently visible (filtered) groups
      let count = 0;
      for (const groups of Object.values(this.slotGroupsByPrayer)) {
        for (const g of groups) count += g.students.length;
      }
      return count;
    },

    totalSlots() {
      // Count visible slot-teacher groups after teacher filter
      let count = 0;
      for (const groups of Object.values(this.slotGroupsByPrayer)) {
        count += groups.length;
      }
      return count;
    },

    totalTeachers() {
      return this.teacherOptions.length;
    },
  },

  methods: {
    async loadScheduleOptions() {
      try {
        const result = await listIqraSchedules({ is_active: true, page: 1, size: 100 });
        this.scheduleOptions = (result?.data || []).map((s) => ({
          id: String(s.id),
          title: s.title || `جدول #${s.id}`,
        }));
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل قائمة الجداول';
      }
    },

    async loadDailyReport() {
      if (!this.selectedScheduleId || !this.selectedDate) {
        this.scheduleDetail = null;
        this.enrollments = [];
        return;
      }

      this.loading = true;
      this.errorMessage = '';
      this.scheduleDetail = null;
      this.enrollments = [];

      try {
        const [scheduleResult, enrollmentsResult] = await Promise.all([
          getIqraSchedule(Number(this.selectedScheduleId)),
          listIqraStudentSlotEnrollments({
            schedule_id: Number(this.selectedScheduleId),
            status: 'active',
            size: 200,
          }),
        ]);

        this.scheduleDetail = scheduleResult;
        this.enrollments = enrollmentsResult?.data || [];
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل التقرير';
      } finally {
        this.loading = false;
      }
    },

    normalizePrayerName(prayerName) {
      const key = String(prayerName || '').trim().toLowerCase();
      if (['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'].includes(key)) return key;
      return 'fixed';
    },

    buildTimeDesc(slot) {
      if (slot.start_mode === 'fixed_time' && slot.fixed_start_time) {
        return `وقت ثابت (${this.formatTimeArabic(String(slot.fixed_start_time).slice(0, 5))})`;
      }
      if (slot.start_mode === 'prayer_offset') {
        const prayerLabel = this.prayerLabel(slot.prayer_name);
        const offset = Number(slot.offset_minutes);
        if (Number.isNaN(offset) || offset === 0) return `وقت ${prayerLabel}`;
        const dir = offset > 0 ? 'بعد' : 'قبل';
        return `${dir} ${prayerLabel} بـ ${Math.abs(offset)} دقيقة`;
      }
      return '';
    },

    prayerLabel(prayerValue) {
      const labels = {
        fajr: 'الفجر',
        dhuhr: 'الظهر',
        asr: 'العصر',
        maghrib: 'المغرب',
        isha: 'العشاء',
      };
      return labels[String(prayerValue || '').trim().toLowerCase()] || 'الصلاة';
    },

    formatTimeArabic(value) {
      const [hText, mText] = String(value || '').split(':');
      const h = Number(hText);
      const m = Number(mText);
      if (Number.isNaN(h) || Number.isNaN(m)) return value || '-';
      const period = h >= 12 ? 'م' : 'ص';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    },

    colorFromName(name) {
      const palette = ['blue', 'amber', 'emerald', 'purple', 'indigo', 'rose'];
      const str = String(name || '');
      let hash = 0;
      for (let i = 0; i < str.length; i += 1) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0;
      }
      return palette[Math.abs(hash) % palette.length];
    },

    groupColorClass(color) {
      const map = {
        blue: 'bg-blue-50 border-blue-400',
        amber: 'bg-amber-50 border-amber-400',
        emerald: 'bg-emerald-50 border-emerald-400',
        purple: 'bg-purple-50 border-purple-400',
        indigo: 'bg-indigo-50 border-indigo-400',
        rose: 'bg-rose-50 border-rose-400',
      };
      return map[color] || map.blue;
    },

    printReport() {
      window.print();
    },
  },

  watch: {
    selectedScheduleId() {
      this.selectedTeacherId = '';
      this.loadDailyReport();
    },
  },
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/icon?family=Material+Icons+Round');

main {
  font-family: 'Noto Kufi Arabic', sans-serif;
}

.material-icons-round {
  font-size: 20px;
}

.daily-grid {
  display: grid;
  grid-template-columns: 110px 1fr;
}

.daily-grid-cols {
  display: grid;
}

@media print {
  header,
  .print-hide {
    display: none !important;
  }

  main {
    background: white;
  }

  .daily-grid {
    break-inside: avoid;
  }
}
</style>
